import { useEffect, useState, useCallback } from "react";
import { Check, X, RotateCcw, RefreshCw } from "lucide-react";
import { Api } from "../../lib/api";

const TAG_STYLES = {
  NEW: "bg-[#FFE7C8] text-[#A24B00]",
  CONFIRMED: "bg-[#C6EBD0] text-[#0A3D1C]",
  CANCELLED: "bg-[#F1C9C9] text-[#7A1E1E]",
};

function tagOf(status) {
  const s = (status || "").toLowerCase();
  if (s === "cancelled") return "CANCELLED";
  if (s === "paid" || s === "confirmed") return "CONFIRMED";
  return "NEW";
}

function StatusBadge({ status }) {
  const tag = tagOf(status);
  return (
    <span
      className={`inline-block rounded-round px-2 py-0.5 text-[10px] font-bold tracking-wider ${TAG_STYLES[tag]}`}
      data-testid={`row-tag-${tag.toLowerCase()}`}
    >
      {tag}
    </span>
  );
}

function RowActions({ booking, busy, onPatch }) {
  return (
    <div className="flex justify-end gap-1">
      <button
        title="Mark CONFIRMED"
        disabled={busy}
        onClick={() => onPatch(booking.id, "confirmed")}
        className="p-1.5 rounded-md hover:bg-[#C6EBD0] disabled:opacity-50"
        data-testid={`confirm-btn-${booking.id}`}
      >
        <Check size={14} />
      </button>
      <button
        title="Mark CANCELLED"
        disabled={busy}
        onClick={() => onPatch(booking.id, "cancelled")}
        className="p-1.5 rounded-md hover:bg-[#F1C9C9] disabled:opacity-50"
        data-testid={`cancel-btn-${booking.id}`}
      >
        <X size={14} />
      </button>
      <button
        title="Reset to NEW"
        disabled={busy}
        onClick={() => onPatch(booking.id, "pending")}
        className="p-1.5 rounded-md hover:bg-[#FFE7C8] disabled:opacity-50"
        data-testid={`reset-btn-${booking.id}`}
      >
        <RotateCcw size={14} />
      </button>
    </div>
  );
}

export default function BookingsTable({ token }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    setErr(null);
    try {
      setRows(await Api.adminBookings(token));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Could not load");
      setRows([]);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  async function onPatch(id, status) {
    setBusy(id);
    try {
      await Api.adminUpdateBookingStatus(token, id, status);
      setRows((rs) => rs?.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Update failed");
    } finally {
      setBusy(null);
    }
  }

  if (!token) return null;
  if (err) {
    return (
      <p className="text-red-600 text-sm" data-testid="bookings-error">
        {err}
      </p>
    );
  }
  if (rows == null) {
    return (
      <p className="text-grey text-sm" data-testid="bookings-loading">
        Loading bookings…
      </p>
    );
  }

  const counts = rows.reduce(
    (acc, r) => ((acc[tagOf(r.status)] += 1), acc),
    { NEW: 0, CONFIRMED: 0, CANCELLED: 0 }
  );

  return (
    <section className="space-y-3" data-testid="bookings-table-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="eyebrow">
          Bookings · {rows.length}
          {" · "}
          <span className="text-[#A24B00]">{counts.NEW} new</span>
          {" · "}
          <span className="text-[#0A3D1C]">{counts.CONFIRMED} confirmed</span>
          {" · "}
          <span className="text-[#7A1E1E]">{counts.CANCELLED} cancelled</span>
        </p>
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1 text-sm text-navy hover:text-coral"
          data-testid="refresh-bookings-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#EFE7D2]">
        <table
          className="min-w-full text-sm"
          data-testid="bookings-table"
        >
          <thead className="bg-navy text-cream/90 text-left text-xs tracking-wide uppercase">
            <tr>
              <th className="p-2.5">Tag</th>
              <th className="p-2.5">Name / Email</th>
              <th className="p-2.5">Day</th>
              <th className="p-2.5 text-center">Qty</th>
              <th className="p-2.5">Method</th>
              <th className="p-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="p-4 text-center text-grey">
                  No bookings yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-[#EFE7D2] align-middle"
                data-testid={`booking-row-${r.id}`}
              >
                <td className="p-2.5">
                  <StatusBadge status={r.status} />
                </td>
                <td className="p-2.5">
                  <div className="font-semibold text-navy">{r.name}</div>
                  <div className="text-grey text-xs">{r.email}</div>
                </td>
                <td className="p-2.5">
                  <div className="text-navy">{r.concept_name}</div>
                  <div className="text-grey text-xs">{r.day_label}</div>
                </td>
                <td className="p-2.5 text-center">{r.quantity}</td>
                <td className="p-2.5 capitalize">{r.payment_method || "—"}</td>
                <td className="p-2.5">
                  <RowActions
                    booking={r}
                    busy={busy === r.id}
                    onPatch={onPatch}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
